/**
 * Add Child chooser — a small launcher shown from a parent conversation.
 * Offers two paths (mirrors the KC2 "Add Child" flow):
 *
 *   • Create new    → opens the full New Conversation form in child mode.
 *   • Link existing → opens the reusable ConversationSearchDrawer to find and
 *                     link an existing conversation as a child.
 *
 * Both heavy flows live in their own drawers, so this stays a thin chooser.
 */
import { type JSX } from 'react'
import { X, Search, Plus, Network } from 'lucide-react'
import { R } from './primitives.js'
import { DrawerInfoButton, DrawerInfoPanel, useDrawerInfo } from '../../ui/DrawerInfo.js'

interface ChildTicketDrawerProps {
  open: boolean
  onClose: () => void
  parentReferenceId?: string | null
  /** Open the full New Conversation form in child mode. */
  onCreateNew: () => void
  /** Open the conversation search drawer to link an existing conversation. */
  onLinkExisting: () => void
}

export default function ChildTicketDrawer({
  open,
  onClose,
  parentReferenceId,
  onCreateNew,
  onLinkExisting,
}: ChildTicketDrawerProps): JSX.Element | null {
  // Before the early return — hooks can't run conditionally.
  const drawerInfo = useDrawerInfo(open)

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative z-10 w-full max-w-xl overflow-hidden ${R} bg-white shadow-2xl`}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <span className={`flex h-9 w-9 items-center justify-center ${R} bg-[#3366FF] text-white shadow-sm`}>
              <Network className="h-[18px] w-[18px]" />
            </span>
            <div>
              <h2 className="text-base font-bold text-[#0f172a]">Add Child Conversation</h2>
              <p className="text-xs text-slate-400">
                Parent {parentReferenceId ? `#${parentReferenceId}` : 'conversation'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <DrawerInfoButton shown={drawerInfo.shown} onToggle={drawerInfo.toggle} />
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className={`flex h-8 w-8 items-center justify-center ${R} text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600`}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <DrawerInfoPanel
          info={{
            lead: `A child conversation tracks one distinct piece of work under ${parentReferenceId ? `#${parentReferenceId}` : 'this conversation'}, with its own reference, owner and SLA clock.`,
            points: [
              { term: 'Create new', detail: 'Opens the full conversation form, pre-filled from the parent with whatever you tick to copy.' },
              { term: 'Link existing', detail: 'Makes a conversation that already exists a child of this one. It keeps its own history.' },
              { term: 'Only one parent', detail: 'A conversation that is already someone else\u2019s child cannot be added here — detach it first.' },
              { term: 'Closing', detail: 'This conversation cannot be closed until every child under it is closed.' },
              { term: 'Merging', detail: 'Neither side of a parent/child pair can take part in a merge while the relationship exists.' },
            ],
          }}
          shown={drawerInfo.shown}
          onDismiss={drawerInfo.toggle}
        />

        {/* Choices */}
        <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2">
          <button
            type="button"
            onClick={onCreateNew}
            className={`group flex flex-col items-center gap-3 ${R} border-2 border-slate-200 bg-white p-7 text-center transition-all hover:border-[#3366FF] hover:bg-[#F5F7FF] hover:shadow-md`}
          >
            <span className={`flex h-14 w-14 items-center justify-center ${R} bg-slate-100 transition-colors group-hover:bg-[#3366FF]`}>
              <Plus className="h-7 w-7 text-slate-500 transition-colors group-hover:text-white" />
            </span>
            <span>
              <span className="block text-sm font-bold text-slate-900 group-hover:text-[#3366FF]">Create new</span>
              <span className="mt-1 block text-xs text-slate-500">Open a fresh child conversation with the full form</span>
            </span>
          </button>
          <button
            type="button"
            onClick={onLinkExisting}
            className={`group flex flex-col items-center gap-3 ${R} border-2 border-slate-200 bg-white p-7 text-center transition-all hover:border-[#3366FF] hover:bg-[#F5F7FF] hover:shadow-md`}
          >
            <span className={`flex h-14 w-14 items-center justify-center ${R} bg-slate-100 transition-colors group-hover:bg-[#3366FF]`}>
              <Search className="h-7 w-7 text-slate-500 transition-colors group-hover:text-white" />
            </span>
            <span>
              <span className="block text-sm font-bold text-slate-900 group-hover:text-[#3366FF]">Add existing</span>
              <span className="mt-1 block text-xs text-slate-500">Search and attach an existing conversation as a child</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}
